/**
 * Pure logic for the first-run flow (NEWS-78).
 *
 * Its own module for the same reason `dial.ts` and `topic-sort.ts` are: the
 * wording below has real branching, and a branch that only ever runs inside a
 * rendered dialog is a branch that only ever gets tested through a browser.
 */

/**
 * Whether the first-run guide should open by itself (FR-20.1, NEWS-421).
 *
 * A pure function here rather than a branch inside `maybeOpenOnboarding`, for
 * the reason this module exists: a decision that only runs against a live store
 * inside a rendered app only ever gets tested through a browser — and this one
 * had **no test at all**, which is how it stayed wrong.
 *
 * **Having no topics is the whole test.** It used to also require no usable
 * provider, on the reasoning that someone with either was an existing user who
 * must not be interrupted. The topic count alone already says that. The provider
 * half was actively harmful: a signed-in `claude-cli` is a fact about the
 * *machine*, true before this app was installed, and says nothing about whether
 * Newsmonger is set up. Anyone arriving with Claude Code or Codex already
 * signed in — which FR-20.5 treats as the *best* case — got no guide, ever.
 *
 * It also made first-run behaviour depend on unrelated host state, which the
 * E2E harness had already been bitten by: NEWS-193 records the suite passing on
 * a dev machine and failing on CI for exactly this reason.
 *
 * `loaded` is essential and not paranoia: `topicCount` is 0 before the first
 * `/api/state` answers too, so without it the wizard flashes at every existing
 * user on every reload.
 *
 * There is no provider gate any more (NM-12): AI-provider configuration is
 * environment-only and there is no Source step, so the decision waits only on
 * `/api/state` having answered — never on a provider probe.
 *
 * **No persisted dismissal (NM-222).** The guide opens whenever there are no
 * topics, *every* session — the owner's call: with zero topics the app does
 * nothing, so the guide should keep offering setup until a topic exists (which is
 * also what makes a reset re-show it). Dismissal is only for the current session
 * (the in-memory `onboarding` store state, which a reload clears), so it does not
 * carry across launches. This supersedes the old persisted per-install "seen"
 * suppression, which now only labels the Settings button (FR-20.3). `suppressed`
 * is a **test-only** hook: the E2E suite seeds a localStorage flag so the wizard
 * does not throw itself over the many specs that run at zero topics — real users
 * never set it, the app only reads it.
 */
export function shouldOpenOnboarding(s: { loaded: boolean; topicCount: number; suppressed: boolean }): boolean {
  if (!s.loaded) return false;
  if (s.suppressed) return false;
  return s.topicCount === 0;
}

/**
 * Continent quick-fills for the Location step (NEWS-394).
 *
 * Six hardcoded strings that fill the free-text field, **not a picker over a
 * dataset** — there is deliberately no place list (FR-35.2). They exist because
 * "a continent is enough" is a claim the hint text makes and nobody believes
 * until they see a continent offered as a real answer.
 */
export const LOCATION_QUICK_PICKS = ['Africa', 'Asia', 'Europe', 'North America', 'Oceania', 'South America'] as const;

/**
 * Where `Continue` goes from the profile picker (NEWS-383).
 *
 * The step owns three pages behind one wizard step, so the primary button has
 * two jobs and this decides which. Extracted rather than inlined in the delegate
 * for the reason the whole module exists: a branch that only runs inside a
 * rendered dialog only ever gets tested through a browser.
 *
 * `page` is clamped, not trusted — it arrives from state that a stale render
 * could have left out of range, and landing on the last page beats rendering
 * nothing.
 */
export function nextProfilePage(page: number, pageCount: number): { page: number; advanceStep: boolean } {
  if (pageCount <= 0) return { page: 0, advanceStep: true };
  const at = Math.max(0, Math.min(pageCount - 1, Math.trunc(page)));
  return at >= pageCount - 1 ? { page: at, advanceStep: true } : { page: at + 1, advanceStep: false };
}

/**
 * The Topics step's running total (NEWS-146, NM-91).
 *
 * There are **two** ways to leave that step with topics, and they behave
 * differently: a ticked starter chip is a reservation that Finish turns into a
 * topic, while anything added in the discovery dialog already exists and is
 * already checking. One combined number would be a lie about the half that can
 * no longer be unticked, so each is named and told apart by what happens next.
 *
 * `added` is a *difference* against the count when the step opened rather than a
 * raw total: onboarding is normally a first run, but Settings can reopen it for
 * someone who already has topics, and those are not something they just added.
 *
 * `existing` is those pre-existing topics, by name (NM-91). Without it, reopening
 * the guide for someone who already watches topics — and picks nothing new — read
 * "None yet", which looks like their data was lost or the wizard is broken. The
 * fix is to name what they already watch and call the this-run selection
 * *"new"*, so the empty case reads as "nothing new picked" rather than "nothing".
 */
export function onboardingCountText(chosen: number, added: number, existing: readonly string[] = []): string {
  const parts: string[] = [];
  if (chosen > 0) parts.push(`${String(chosen)} chosen, created when you finish`);
  if (added > 0) parts.push(`${String(added)} added already and checking`);
  const run =
    parts.length > 0 ? `${parts.join(' · ')}. Each is checked on its own, so more topics means more checks.` : '';

  if (existing.length > 0) {
    const preface = `You already watch ${countNoun(existing.length, 'topic')}: ${joinTopicNames(existing)}.`;
    return run === '' ? `${preface} No new topics selected yet.` : `${preface} ${run}`;
  }
  return run === '' ? 'None yet — that’s fine, you can add topics from the sidebar.' : run;
}

/**
 * The interests picked **this run** — those saved now but not when the guide
 * opened (NM-248).
 *
 * Onboarding can be reopened for an existing account, whose `settings.profiles`
 * already carries past picks; `atOpen` is the snapshot taken on entry. The
 * difference is what the user just added, and only those derive new topics on
 * Finish — so changing interests adds the new ones, not the whole set. On a first
 * run `atOpen` is empty and every pick is new, which preserves first-run
 * behaviour. Order-preserving over `current` so the derived topics follow the
 * user's picks; deduped defensively.
 */
export function newlyPickedProfiles(current: readonly string[], atOpen: readonly string[]): string[] {
  const had = new Set(atOpen);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of current) {
    if (had.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * The Topics-step primary button's label (NM-91, NM-244/248).
 *
 * The generic finish label is "Start watching", which is right for a first run.
 * But when the guide is reopened for someone who already watches topics, finishing
 * with nothing new ticked simply keeps what they have — so "Start watching" (or a
 * bare "Finish") misreads as though it might replace or discard it. "Keep current
 * topics" says the truthful thing; once something new is selected it becomes "Add
 * selected topics", because that is now what Finish does.
 *
 * `chosen` counts everything Finish will actually add this run — ticked starter
 * chips **plus** interests picked this run (NM-248's newly-picked profiles) — so
 * the label never says "Keep current topics" over a Finish that would add topics
 * (NM-244), and never says "Add selected topics" when nothing new was picked.
 */
export function onboardingFinishLabel(chosen: number, hasExisting: boolean): string {
  if (!hasExisting) return 'Start Watching';
  return chosen > 0 ? 'Add Selected Topics' : 'Keep Current Topics';
}

/** "1 topic" / "3 topics" — a count with its noun pluralised. */
function countNoun(n: number, noun: string): string {
  return `${String(n)} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * A short, human list of topic names, capped so a long watch-list doesn't run the
 * running-count line off the dialog: up to three named, then "and N more topics".
 */
function joinTopicNames(names: readonly string[]): string {
  const shown = names.slice(0, 3);
  const extra = names.length - shown.length;
  if (extra > 0) return `${shown.join(', ')}, and ${countNoun(extra, 'more topic')}`;
  if (shown.length <= 1) return shown[0] ?? '';
  if (shown.length === 2) return `${shown[0]} and ${shown[1]}`;
  return `${shown.slice(0, -1).join(', ')}, and ${shown[shown.length - 1]}`;
}
